import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Building2, Clock, ExternalLink, Trash2 } from "lucide-react";

interface Application {
  id: string;
  status: string;
  applied_at: string;
  last_status_change: string;
  interview_date: string | null;
  notes: string | null;
  opportunity: {
    id: string;
    title: string;
    company_name: string;
    location: string;
    employment_type: string;
  };
}

export default function ApplicationsTracker() {
  const { toast } = useToast();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("job_applications")
        .select(`
          *,
          opportunity:opportunities (
            id,
            title,
            company_name,
            location,
            employment_type
          )
        `)
        .eq("candidate_id", user.id)
        .order("applied_at", { ascending: false });

      if (error) throw error;

      setApplications((data as any) || []);
    } catch (error: any) {
      console.error("Fetch error:", error);
      toast({
        title: "Error Loading Applications",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (applicationId: string) => {
    try {
      const { error } = await supabase
        .from("job_applications")
        .update({ status: "withdrawn" })
        .eq("id", applicationId);

      if (error) throw error;

      toast({
        title: "Application Withdrawn",
        description: "Your application has been withdrawn.",
      });

      fetchApplications();
    } catch (error: any) {
      console.error("Withdraw error:", error);
      toast({
        title: "Error Withdrawing Application",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
      applied: { label: "Applied", variant: "secondary" },
      under_review: { label: "Under Review", variant: "default" },
      interview_scheduled: { label: "Interview Scheduled", variant: "default" },
      interviewed: { label: "Interviewed", variant: "default" },
      offer_received: { label: "Offer Received", variant: "default" },
      rejected: { label: "Rejected", variant: "destructive" },
      withdrawn: { label: "Withdrawn", variant: "outline" },
      accepted: { label: "Accepted", variant: "default" },
    };

    const config = statusConfig[status] || statusConfig.applied;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const filteredApplications = applications.filter((app) => {
    if (filter === "all") return true;
    if (filter === "active") return !["rejected", "withdrawn", "accepted"].includes(app.status);
    return app.status === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>My Applications</CardTitle>
          <CardDescription>Track your job applications and their status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap mb-6">
            <Button
              size="sm"
              variant={filter === "all" ? "default" : "outline"}
              onClick={() => setFilter("all")}
            >
              All ({applications.length})
            </Button>
            <Button
              size="sm"
              variant={filter === "active" ? "default" : "outline"}
              onClick={() => setFilter("active")}
            >
              Active ({applications.filter(a => !["rejected", "withdrawn", "accepted"].includes(a.status)).length})
            </Button>
            <Button
              size="sm"
              variant={filter === "interview_scheduled" ? "default" : "outline"}
              onClick={() => setFilter("interview_scheduled")}
            >
              Interviews ({applications.filter(a => a.status === "interview_scheduled").length})
            </Button>
          </div>
        </CardContent>
      </Card>

      {filteredApplications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No applications found.</p>
            <p className="text-sm text-muted-foreground mt-2">
              Start applying to jobs to see them here!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredApplications.map((app) => (
            <Card key={app.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <CardTitle className="text-lg">{app.opportunity.title}</CardTitle>
                    <CardDescription className="text-base font-medium text-foreground">
                      {app.opportunity.company_name}
                    </CardDescription>
                  </div>
                  {getStatusBadge(app.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{app.opportunity.location}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="capitalize">{app.opportunity.employment_type.replace("-", " ")}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>Applied {new Date(app.applied_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {app.interview_date && (
                  <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Interview scheduled for {new Date(app.interview_date).toLocaleString()}
                    </p>
                  </div>
                )}

                {app.notes && (
                  <div className="text-sm text-muted-foreground">
                    <strong>Notes:</strong> {app.notes}
                  </div>
                )}

                <div className="flex gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" className="flex-1">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Job
                  </Button>
                  {app.status === "applied" || app.status === "under_review" ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleWithdraw(app.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Withdraw
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
